/* eslint-disable consistent-return */
import { readonly, ref, useContext } from '@nuxtjs/composition-api';
import type {
    UseStoreInterface, UseStroreErrors, CityParams, FindStoreParams, detailStoreParams, CityResults, findStoreByProductParams
} from './useStore';
import { Logger } from "~/helpers/logger";
import { getApiAxios } from "~/helpers";
import api from "~/config/extensionsApi";


/**
 * Allows loading and adding product reviews.
 *
 * See the {@link UseReviewInterface} for a list of methods and values available in this composable.
 */
export function useStore(): UseStoreInterface {
    const loading = ref(false);
    const error = ref<UseStroreErrors>({
        listCity: null,
        findStore: null,
        detailStore: null,
        topStores: null,
        findStoreByProduct: null
    });

    const listCityData = ref<CityResults[]>([]);

    async function listCity(params: CityParams): Promise<void> {
        try {
            loading.value = true;
            if (!params.country_id) {
                Logger.error('listCity/useStore: ', 'params country_id is required');
            } else {
                const response = await getApiAxios(api.storelocator.storelocator_city, {}, {
                    country_id: params.country_id
                });

                if (response && response.result && response.result.length > 0) {
                    listCityData.value = response.result;
                }
            }

        } catch (error) {
            console.error("Error fetching list data city:", error);
        } finally {
            loading.value = false;
        }
    };

    const findStore = async (params: FindStoreParams) => {
        if (!params.city_id) {
            Logger.error('findStore/useStore: ', 'params city_id is required');
            return;
        }

        let results = null;
        try {
            loading.value = true;
            const response = await getApiAxios(api.storelocator.storelocator_find_stores, {}, {
                city_id: params.city_id
            });

            const json = response;
            if (json.code !== 200 || json.result.errors) {
                error.value.findStore = json.result.errors;
                Logger.error('findStore/useStore: ', json.result);
            } else {
                results = json?.result ?? null;
                error.value.findStore = null;
            }
        } catch (err) {
            error.value.findStore = err;
            Logger.error('findStore/useStore', err);

        } finally {
            loading.value = false;
        }

        return results;

    };

    const detailStore = async (params: detailStoreParams) => {
        if (!params.store_id) {
            Logger.error('detailStore/useStore: ', 'params store_id is required');
            return;
        }

        let results = null;
        try {
            loading.value = true;
            const response = await getApiAxios(api.storelocator.storelocator_get_store_detail, {}, {
                store_id: params.store_id
            });

            const json = response;
            if (json.code !== 200 || json.result.errors) {
                error.value.detailStore = json.result.errors;
                Logger.error('detailStore/useStore: ', json.result);
            } else {
                results = json?.result ?? null;
                error.value.detailStore = null;
            }
        } catch (err) {
            error.value.detailStore = err;
            Logger.error('detailStore/useStore', err);

        } finally {
            loading.value = false;
        }

        return results;

    };

    const findStoreByProduct = async (params: findStoreByProductParams) => {
        if (!params.city_id || !params.sku || !params.color || !params.city_id) {
            Logger.error('useStore/findStoreByProduct: ', 'params city_id, sku, color, city_id is required');
            return;
        }

        let results = null;
        try {
            loading.value = true;
            const response = await getApiAxios(api.storelocator.storelocator_find_product, {}, {
                city_id: params.city_id,
                sku: params.sku,
                color: params.color,
                size: params.size
            });

            const json = response;
            if (json.code !== 200 || json.result.errors) {
                error.value.detailStore = json.result.errors;
                Logger.error('useStore/findStoreByProduct: ', json.result);
            } else {
                results = json?.result ?? null;
                error.value.findStoreByProduct = null;
            }
        } catch (err) {
            error.value.findStoreByProduct = err;
            Logger.error('useStore/useStore', err);

        } finally {
            loading.value = false;
        }

        return results;

    };

    const topStores = async () => {
        let results = null;
        try {
            loading.value = true;
            const response = await getApiAxios(api.storelocator.storelocator_top_stores);

            const json = response;
            if (json.code !== 200 || json.result.errors) {
                error.value.topStores = json.result.errors;
                Logger.error('topStores/useStore: ', json.result);
            } else {
                results = json?.result ?? null;
                error.value.topStores = null;
            }
        } catch (err) {
            error.value.topStores = err;
            Logger.error('topStores/useStore', err);

        } finally {
            loading.value = false;
        }

        return results;

    };

    return {
        listCity,
        findStore,
        detailStore,
        listCityData,
        topStores,
        findStoreByProduct,
        loading: readonly(loading),
        error: readonly(error),
    };
}

export default useStore;
export * from './useStore';